import { useState, useContext, useMemo } from "react";
import {
  Text,
  Flex,
  Icon,
  IconButton,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  Button,
  Image,
  Tag,
  theme,
  Grid,
} from "@chakra-ui/react";
import { text as textTheme } from "theme/foundations/text";
import toast from "react-hot-toast";
import { useSession } from "next-auth/client";
import { format } from "date-fns";
import { FiTwitter } from "react-icons/fi";
import { FaRegComment } from "react-icons/fa";
import { BsCheckLg } from "react-icons/bs";
import { GrResume } from "react-icons/gr";
import { BsChatSquareDots } from "react-icons/bs";
import { MdPendingActions, MdDownloadDone } from "react-icons/md";
import { RiDraftLine } from "react-icons/ri";
import { SettingsPopup } from "./settings-popup";
import { DeleteTweet } from "../popups/delete-tweet";
import { DmHistoryPopup } from "../popups/dm-history-popup";
import { firebaseClient } from "firebaseClient";
import { TweetContext } from "context/tweetContext";
import { getAccount } from "utils/sessionHelper";
import { checkTweetValid, scheduleTweet } from "controllers/schedule";
import { approve, reject } from "controllers/ghostController";
import { generate } from "controllers/variations";
import { isAllowed } from "controllers/subscription";
import { StatsPopup } from "./stats-popup";
import { Reports } from "./reports";
import { ConfirmPopup } from "components/popups/confirmPopup";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { useWindowWidth } from "@react-hook/window-size";
import { radius, textStyle } from "theme/names";
import { border } from "theme/foundations/border";
import { AIVariationsIcon } from "components/icons/AIvariations";
import { PlusIcon } from "components/icons/PlusIcon";

interface Props {
  type: "published" | "drafts" | "ghost";
  tweet: any;
  time?: Date;
  bg?: any;
  threadBg?: any;
  onDelete?: (docId: string) => void;
  isGhostwriter?: boolean;
  isOrgDrafts?: boolean;
  disableIcon?: boolean;
  disableGhostInfo?: boolean;
}

export function TweetBar({
  tweet,
  time,
  type,
  bg = useColorModeValue("gray.50", "gray.800"),
  threadBg = bg,
  onDelete = () => {},
  isGhostwriter = false,
  isOrgDrafts = false,
  disableIcon = false,
  disableGhostInfo = false,
}: Props) {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState<boolean>(false);
  const [isLoadingAutoDmStatus, setIsLoadingAutoDmStatus] =
    useState<boolean>(false);
  const [isLoadingVariation, setIsLoadingVariation] = useState<boolean>(false);
  const {
    isOpen: isOpenRevision,
    onOpen: onOpenRevision,
    onClose: onCloseRevision,
  } = useDisclosure();
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = useDisclosure();
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenStats,
    onClose: onCloseStats,
    onOpen: onOpenStats,
  } = useDisclosure();
  const {
    isOpen: isOpenReports,
    onClose: onCloseReports,
    onOpen: onOpenReports,
  } = useDisclosure();
  const {
    isOpen: isOpenDmHistory,
    onClose: onCloseDmHistory,
    onOpen: onOpenDmHistory,
  } = useDisclosure();
  const [session] = useSession();
  const tweetContext: any = useContext(TweetContext);
  const iconColor = useColorModeValue("gray.600", "gray.400");
  const isThread = tweet?.text?.includes("\n\n\n");
  const miscellaneousContext: any = useMiscellaneousContext();
  const screenWidth = useWindowWidth();

  const handleAddToQueue = async () => {
    let dataToSend = {
      idUser: getAccount(session)?.id,
      idAccount: getAccount(session)?.idAccount,
      twUserName: getAccount(session)?.twUserName,
      id: tweet.id,
      text: tweet.text,
      isScheduled: true,
      scheduledTime: tweetContext.nextSlot,
      isAutoPlug: false,
      isAutoRetweet: getAccount(session)?.isAutoRetweet ?? false,
      autoRetweetHours: getAccount(session)?.autoRetweetHours ?? 12,
      autoRetweetTimes: getAccount(session)?.autoRetweetTimes ?? 1,
      isPreventLinkExpand: getAccount(session)?.isPreventLinkExpand ?? 1,
      isPreventLinkExpandAutoPlug:
        getAccount(session)?.isPreventLinkExpand ?? 1,
      isRetweeted: false,
      isPlugged: false,
      autoRtWithOtherAccounts:
        getAccount(session)?.autoRtWithOtherAccounts ?? false,
    };

    if (
      !(await checkTweetValid([], session, tweetContext, dataToSend, undefined))
    ) {
      return;
    }
    await scheduleTweet(
      session,
      undefined,
      undefined,
      dataToSend,
      getAccount(session)?.timezone,
      tweetContext
    );
    // tweetContext.refreshNextSlot();
    tweetContext.refresher({});
  };

  const handleAiGeneration = async () => {
    if (
      session?.user?.data?.skipPopupVariationAuto &&
      (session?.user?.data?.creditsAuto ||
        isAllowed(session.user, "ai_variation"))
    ) {
      setIsLoadingVariation(true);
      let result = await generate(session, { full_text: tweet.text });
      setIsLoadingVariation(false);

      if (result?.success && result?.data?.variations?.length) {
        let newVariation = result?.data?.variations[0];
        tweetContext.newTweet({ text: newVariation });
        tweetContext.setIsTweetTextChanged(true);
        tweetContext.open();
        toast.success("Variation pasted in tweet composer");
      }
    } else {
      if (!tweet.full_text) {
        tweet.full_text = tweet.text;
      }
      tweetContext.requestVariationAuto(tweet.data || tweet);
    }
  };

  const tweetLabels = useMemo(
    () => getAccount(session)?.tweetLabels,
    [session, miscellaneousContext.refreshLabels]
  );
  const labelsLinkedToTweet = tweetLabels?.filter((l) =>
    tweet?.labels?.includes(l.id)
  );

  let icon = (
    // <Box pt={2}>
    //   <Icon as={AiFillTwitterCircle} fontSize="xl" color="blue.400" />
    // </Box>
    <Grid
      {...{
        w: 6,
        placeContent: "center",
        ml: ["drafts", "ghost"].includes(type) ? 4 : 0,
      }}
    >
      <FiTwitter {...{ fontSize: "18px", color: "#5A97DB" }} />
    </Grid>
  );

  if (isThread) {
    // icon = <>🧵</>;
    icon = (
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 5.5V4.5H12V5.5H2ZM0.75 1.5C0.3125 1.5 0 1.1875 0 0.75C0 0.34375 0.3125 0 0.75 0H13.25C13.6562 0 14 0.34375 14 0.75C14 1.1875 13.6562 1.5 13.25 1.5H12V3.5H2V1.5H0.75ZM3.5 1.5V2.5H10.5V1.5H3.5ZM12 8.5V9.5H2V8.5H12ZM13.5 6.5C13.75 6.5 14 6.75 14 7C14 7.28125 13.75 7.5 13.5 7.5H2V6.5H13.5ZM13.25 14.5C13.6562 14.5 14 14.8438 14 15.25C14 15.6875 13.6562 16 13.25 16H0.75C0.3125 16 0 15.6875 0 15.25C0 14.8438 0.3125 14.5 0.75 14.5H2V12.5H12V14.5H13.25ZM10.5 14.5V13.5H3.5V14.5H10.5ZM2 11.5V10.5H12V11.5H2Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  let iconStatus = RiDraftLine;
  let iconStatusColor = "gray.400";
  if (tweet.statusGhost == "pending") {
    iconStatus = MdPendingActions;
    iconStatusColor = "gray.400";
  }
  if (tweet.statusGhost == "done" || tweet.statusGhost == "ready") {
    iconStatus = MdDownloadDone;
    iconStatusColor = "green.400";
  }
  if (tweet.statusGhost == "rejected") {
    iconStatus = BsChatSquareDots;
    iconStatusColor = "red.400";
  }

  let bgColorDefault = useColorModeValue("white", "#1E1E1E");

  return (
    <Flex
      key={tweet.id ?? time}
      // rounded="xl"
      // mb={5}
      // p={2}
      // pl={5}
      // justifyContent="space-between"
      // alignItems={{ base: "start", lg: "center" }}
      // bg={isThread ? threadBg : bg}
      // flexDirection={{ base: "column", lg: "row" }}
      // cursor="pointer"
      borderRadius={radius.lg}
      mb={5}
      p={2}
      alignItems="center"
      bg={bgColorDefault}
      _hover={{
        boxShadow:
          "0px 0px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)",
      }}
      {...border.default.light}
      {...textTheme.body.medium.light.light}
      _dark={{
        ...border.default.dark,
        ...textTheme.body.medium.light.dark,
      }}
      cursor="pointer"
      onClick={onOpenSettings}
    >
      {/* <Flex alignItems="center" maxW={{ base: "95%", lg: "60%" }}> */}
      {/* published icon */}
      {type === "published" && (
        <Grid
          {...{
            display: ["none", "grid"], // hide on very small screens
            h: "32px",
            w: "32px",
            minW: "32px",
            bg: "success.lightMode.100",
            _dark: {
              bg: "success.darkMode.100",
            },
            borderRadius: radius.sm,
            fontSize: "14px",
            placeContent: "center",
          }}
        >
          <Icon
            {...{
              viewBox: "0 0 14 10",
            }}
          >
            <svg
              width="14"
              height="10"
              viewBox="0 0 14 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.9766 0.648438C13.332 0.976562 13.332 1.55078 12.9766 1.87891L5.97656 8.87891C5.64844 9.23438 5.07422 9.23438 4.74609 8.87891L1.24609 5.37891C0.890625 5.05078 0.890625 4.47656 1.24609 4.14844C1.57422 3.79297 2.14844 3.79297 2.47656 4.14844L5.34766 7.01953L11.7461 0.648438C12.0742 0.292969 12.6484 0.292969 12.9766 0.648438Z"
                fill="#31C52E"
              />
            </svg>
          </Icon>
        </Grid>
      )}
      {/* draft icon */}
      {type === "drafts" && (
        <Grid
          {...{
            display: ["none", "grid"], // hide on very small screens
            h: "32px",
            w: "32px",
            minW: "32px",
            bg: "background.lightMode.medium",
            _dark: {
              bg: "background.darkMode.medium",
            },
            borderRadius: radius.sm,
            fontSize: "14px",
            placeContent: "center",
          }}
        >
          <Icon
            {...{
              viewBox: "0 0 17 15",
            }}
          >
            <svg
              width="17"
              height="15"
              viewBox="0 0 17 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.98438 10.6211C2.09375 10.2383 2.3125 9.88281 2.58594 9.60938L10.8984 1.29688C11.582 0.613281 12.7031 0.613281 13.3867 1.29688L14.4531 2.36328C14.5352 2.44531 14.6172 2.55469 14.6719 2.63672C15.1367 3.32031 15.0547 4.25 14.4531 4.85156L6.14062 13.1641C6.11328 13.1914 6.05859 13.2188 6.03125 13.2734C5.75781 13.4922 5.45703 13.6562 5.12891 13.7656L1.82031 14.7227C1.60156 14.8047 1.35547 14.75 1.19141 14.5586C1 14.3945 0.945312 14.1484 1 13.9297L1.98438 10.6211ZM2.61328 13.1367L4.74609 12.5078C4.91016 12.4531 5.07422 12.3711 5.21094 12.2344L11.4727 5.97266L9.75 4.27734L3.51562 10.5391C3.48828 10.5391 3.48828 10.5664 3.46094 10.5938C3.35156 10.7031 3.29688 10.8398 3.24219 11.0039L2.61328 13.1367ZM16.0938 13.4375C16.4492 13.4375 16.75 13.7383 16.75 14.0938C16.75 14.4766 16.4492 14.75 16.0938 14.75H7.78125C7.39844 14.75 7.125 14.4766 7.125 14.0938C7.125 13.7383 7.39844 13.4375 7.78125 13.4375H16.0938Z"
                fill="#9E9E9E"
              />
            </svg>
          </Icon>
        </Grid>
      )}
      {type === "published" && time && (
        <Text
          textStyle={textStyle["body.medium.light"]}
          w="80px"
          minW="80px"
          ml={[2, 4]}
        >
          {format(time, "hh:mm aaa")}
        </Text>
      )}
      {["ghost"].includes(type) && (
        <Icon mr={0} color={iconStatusColor} fontSize="lg" as={iconStatus} />
      )}
      {!disableIcon && icon}
      {!disableGhostInfo && tweet?.ghostwriterTwUserName && (
        <Flex ml={5} my={{ base: "3", lg: "0" }}>
          <Image
            mr={2}
            src={tweet.ghostwriterImage}
            rounded="full"
            w="6"
            h="6"
          />
          {screenWidth > 1100 && <Text>@{tweet.ghostwriterTwUserName}</Text>}
        </Flex>
      )}
      {(isGhostwriter || isOrgDrafts) && (
        <Flex ml={5} my={{ base: "3", lg: "0" }} alignItems={"center"}>
          <Image
            mr={2}
            src={tweet.image ?? tweet.profilePic}
            rounded="full"
            w="6"
            h="6"
          />
          {screenWidth > 1100 && (
            <Text textStyle="body.medium.light">@{tweet.twUserName}</Text>
          )}
        </Flex>
      )}
      {tweet && (
        // <Text minW="400px" w="600px" fontWeight="600" ml={6} overflow="hidden" noOfLines={1} wordBreak="break-all">
        <Text
          // w={{ base: "100%", md: "300px", lg: "150px", xl: "400px" }} fontWeight="600" ml={6} overflow="hidden" noOfLines={1} wordBreak="break-all"
          textStyle={textStyle["body.medium.light"]}
          ml={4}
          mr={12}
          flex={1}
          noOfLines={1}
          wordBreak={"break-all"}
          {...textTheme.body.medium.standard.light}
          _dark={{
            ...textTheme.body.medium.standard.dark,
          }}
        >
          {tweet.text}
        </Text>
      )}
      {/* </Flex> */}
      <Flex
        alignItems="center"
        justifyContent="space-evenly"
        w={{ base: "100%", lg: "unset" }}
        mt={{ base: "1.5", lg: "0" }}
      >
        {type === "drafts" && labelsLinkedToTweet?.length > 0 && (
          <Flex mr="2">
            {labelsLinkedToTweet.map((l) => (
              <Tag key={l.id} mr={2} variant="outline" colorScheme={l.color}>
                {l.title}
              </Tag>
            ))}
          </Flex>
        )}
        {type === "published" && tweet.status == "sent" && tweet.id_str && (
          <>
            {tweet.isAutoDm && (
              <Flex
                alignItems="center"
                border={
                  "solid 1px " +
                  (tweet.autoDmStatus === "error"
                    ? theme.colors.red[400]
                    : theme.colors.gray[200])
                }
                rounded="lg"
                p={0}
                px={2}
                mr={2}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDmHistory();
                }}
                cursor="pointer"
              >
                <Text mr="2">
                  {tweet.autoDmSent ?? 0} {tweet.autoDmSent > 1 ? "DMs" : "DM"}{" "}
                  sent
                </Text>
                {["done", "error"].includes(tweet.autoDmStatus) && (
                  <Tooltip
                    label={
                      <>
                        Auto-DM has the status{" "}
                        {"'" +
                          tweet.autoDmStatus +
                          "'" +
                          (tweet.message?.includes("Auto-DM error")
                            ? " with message: " +
                              tweet.message.replace("Auto-DM error: ", "")
                            : "")}
                        <br></br>
                        <br></br>This button will force resume the auto-DMs
                      </>
                    }
                  >
                    <IconButton
                      aria-label="force resume"
                      variant={"secondary"}
                      size="sm"
                      icon={
                        <Icon color={iconColor} fontSize="sm" as={GrResume} />
                      }
                      isLoading={isLoadingAutoDmStatus}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setIsLoadingAutoDmStatus(true);
                        const db = firebaseClient.firestore();
                        await db
                          .collection("users")
                          .doc(getAccount(session)?.id)
                          .collection("tweets")
                          .doc(tweet.id)
                          .update({ autoDmStatus: "running" });
                        setIsLoadingAutoDmStatus(false);
                        toast.success("Auto-DM is now running again");
                      }}
                    />
                  </Tooltip>
                )}
              </Flex>
            )}
            <Tooltip label="Edit tweet">
              <IconButton
                aria-label="edit tweet"
                // color="twitter.600"
                variant={"action"}
                size="table"
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.4688 5.5625C20.1562 6.25 20.1562 7.34375 19.4375 8.03125L12.6562 14.8125C12.4375 15.0312 12.1562 15.1875 11.8438 15.2812C10.7812 15.5312 8.46875 16 8.46875 16C8.4375 16 8.4375 16 8.40625 16C8.15625 16 7.9375 15.7812 8 15.5L8.75 12.125C8.8125 11.8125 8.96875 11.5 9.21875 11.2812L15.9688 4.53125C16.3125 4.1875 16.75 4 17.2188 4C17.6562 4 18.0938 4.1875 18.4375 4.53125L19.4688 5.5625ZM18.4062 6.96875C18.5 6.875 18.5 6.71875 18.4062 6.625L17.375 5.59375C17.3125 5.53125 17.25 5.5 17.2188 5.5C17.1562 5.5 17.0938 5.53125 17.0312 5.59375L10.2812 12.3438C10.25 12.375 10.2188 12.4062 10.2188 12.4688L9.8125 14.1875C10.4375 14.0625 11.0938 13.9062 11.5312 13.8125C11.5625 13.8125 11.5938 13.7812 11.625 13.75L18.4062 6.96875ZM17.25 13C17.6562 13 18 13.3438 18 13.75L17.9062 17.75C17.9062 19 16.9062 20 15.6875 20H6.21875C5 20 4 19 4 17.75V8.25C4 7.03125 5 6 6.21875 6H10.75C11.1562 6 11.5 6.34375 11.5 6.75C11.5 7.1875 11.1562 7.5 10.75 7.5H6.25C5.8125 7.5 5.5 7.84375 5.5 8.25V17.75C5.5 18.1875 5.8125 18.5 6.25 18.5H15.75C16.1562 18.5 16.5 18.1875 16.5 17.75V13.75C16.5 13.3438 16.8125 13 17.25 13Z"
                      fill="currentColor"
                    />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                  tweetContext.newTweet(tweet);
                  tweetContext.setIsTweetTextChanged(true);
                  tweetContext.open();
                }}
              />
            </Tooltip>
            <Tooltip label="Open on twitter">
              <IconButton
                aria-label="Open on twitter"
                variant="action"
                size="table"
                as="a"
                target="_blank"
                href={
                  "https://twitter.com/" +
                  tweet.twUserName +
                  "/status/" +
                  tweet.id_str
                }
                mx={2}
                icon={
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.75 10C13.1562 10 13.5 10.3438 13.5 10.75V14.25C13.5 15.2188 12.6875 16 11.75 16H2.25C1.28125 16 0.5 15.2188 0.5 14.25V4.75C0.5 3.8125 1.28125 3 2.25 3H5.75C6.15625 3 6.5 3.34375 6.5 3.75C6.5 4.1875 6.15625 4.5 5.75 4.5H2.25C2.09375 4.5 2 4.625 2 4.75V14.25C2 14.4062 2.09375 14.5 2.25 14.5H11.75C11.875 14.5 12 14.4062 12 14.25V10.75C12 10.3438 12.3125 10 12.75 10ZM15.75 0C16.1562 0 16.5 0.34375 16.5 0.75V6C16.5 6.4375 16.1562 6.75 15.75 6.75C15.3125 6.75 15 6.4375 15 6V2.5625L7.25 10.2812C7.125 10.4375 6.9375 10.5 6.75 10.5C6.53125 10.5 6.34375 10.4375 6.21875 10.2812C5.90625 10 5.90625 9.53125 6.21875 9.21875L13.9375 1.5H10.5C10.0625 1.5 9.75 1.1875 9.75 0.75C9.75 0.34375 10.0625 0 10.5 0H15.75Z"
                      fill="currentColor"
                    />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Tooltip>
            <Tooltip label="Stats">
              <IconButton
                aria-label="Stats"
                variant="action"
                size="table"
                mr={2}
                icon={
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 0C8.3125 0 9 0.6875 9 1.5V12.5C9 13.3438 8.3125 14 7.5 14H6.5C5.65625 14 5 13.3438 5 12.5V1.5C5 0.6875 5.65625 0 6.5 0H7.5ZM7.5 1.5H6.5V12.5H7.5V1.5ZM2.5 6C3.3125 6 4 6.6875 4 7.5V12.5C4 13.3438 3.3125 14 2.5 14H1.5C0.65625 14 0 13.3438 0 12.5V7.5C0 6.6875 0.65625 6 1.5 6H2.5ZM2.5 7.5H1.5V12.5H2.5V7.5ZM10 3.5C10 2.6875 10.6562 2 11.5 2H12.5C13.3125 2 14 2.6875 14 3.5V12.5C14 13.3438 13.3125 14 12.5 14H11.5C10.6562 14 10 13.3438 10 12.5V3.5ZM11.5 12.5H12.5V3.5H11.5V12.5Z"
                      fill="currentColor"
                    />
                  </svg>
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenStats();
                }}
              />
            </Tooltip>

            <IconButton
              aria-label="Settings"
              variant="action"
              size="table"
              mr={2}
              icon={
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 13C0 12.5938 0.3125 12.25 0.75 12.25H2.59375C2.90625 11.25 3.875 10.5 5 10.5C6.09375 10.5 7.0625 11.25 7.375 12.25H15.25C15.6562 12.25 16 12.5938 16 13C16 13.4375 15.6562 13.75 15.25 13.75H7.375C7.0625 14.7812 6.09375 15.5 5 15.5C3.875 15.5 2.90625 14.7812 2.59375 13.75H0.75C0.3125 13.75 0 13.4375 0 13ZM6 13C6 12.4688 5.53125 12 5 12C4.4375 12 4 12.4688 4 13C4 13.5625 4.4375 14 5 14C5.53125 14 6 13.5625 6 13ZM11 5.5C12.0938 5.5 13.0625 6.25 13.375 7.25H15.25C15.6562 7.25 16 7.59375 16 8C16 8.4375 15.6562 8.75 15.25 8.75H13.375C13.0625 9.78125 12.0938 10.5 11 10.5C9.875 10.5 8.90625 9.78125 8.59375 8.75H0.75C0.3125 8.75 0 8.4375 0 8C0 7.59375 0.3125 7.25 0.75 7.25H8.59375C8.90625 6.25 9.875 5.5 11 5.5ZM12 8C12 7.46875 11.5312 7 11 7C10.4375 7 10 7.46875 10 8C10 8.5625 10.4375 9 11 9C11.5312 9 12 8.5625 12 8ZM15.25 2.25C15.6562 2.25 16 2.59375 16 3C16 3.4375 15.6562 3.75 15.25 3.75H8.375C8.0625 4.78125 7.09375 5.5 6 5.5C4.875 5.5 3.90625 4.78125 3.59375 3.75H0.75C0.3125 3.75 0 3.4375 0 3C0 2.59375 0.3125 2.25 0.75 2.25H3.59375C3.90625 1.25 4.875 0.5 6 0.5C7.09375 0.5 8.0625 1.25 8.375 2.25H15.25ZM5 3C5 3.5625 5.4375 4 6 4C6.53125 4 7 3.5625 7 3C7 2.46875 6.53125 2 6 2C5.4375 2 5 2.46875 5 3Z"
                    fill="currentColor"
                  />
                </svg>
              }
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings();
              }}
            />
          </>
        )}

        {["ghost"].includes(type) &&
          tweet.messageTotal > 0 &&
          (!tweet["messageRead_" + session?.user?.uid] ||
            tweet.messageTotal >
              tweet["messageRead_" + session?.user?.uid]) && (
            <Flex alignItems="center" mr={2}>
              {tweet.messageTotal -
                (tweet["messageRead_" + session?.user?.uid] ?? 0)}
              <Icon ml={2} color={iconColor} as={FaRegComment} />
            </Flex>
          )}
        {["drafts", "ghost"].includes(type) && (
          <>
            {/* <IconButton
              aria-label="View"
              variant="action"
              size="table"
              mr="2"
              icon={<Icon as={AiFillEye} color={iconColor} />}
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings();
              }}
            /> */}
            <Tooltip hasArrow label="Preview">
              <IconButton
                aria-label="Preview"
                // color="twitter.600"
                variant="action"
                size="table"
                mr={2}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenSettings();
                }}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 12C8 9.8125 9.78125 8 12 8C14.1875 8 16 9.8125 16 12C16 14.2188 14.1875 16 12 16C9.78125 16 8 14.2188 8 12ZM12 14.5C13.375 14.5 14.5 13.4062 14.5 12C14.5 10.625 13.375 9.5 12 9.5C11.9688 9.5 11.9375 9.5 11.9062 9.5C11.9688 9.6875 12 9.84375 12 10C12 11.125 11.0938 12 10 12C9.8125 12 9.65625 12 9.5 11.9375C9.5 11.9688 9.5 12 9.5 12C9.5 13.4062 10.5938 14.5 12 14.5ZM5.96875 7.53125C7.4375 6.15625 9.46875 5 12 5C14.5 5 16.5312 6.15625 18 7.53125C19.4688 8.875 20.4375 10.5 20.9062 11.625C21 11.875 21 12.1562 20.9062 12.4062C20.4375 13.5 19.4688 15.125 18 16.5C16.5312 17.875 14.5 19 12 19C9.46875 19 7.4375 17.875 5.96875 16.5C4.5 15.125 3.53125 13.5 3.0625 12.4062C2.96875 12.1562 2.96875 11.875 3.0625 11.625C3.53125 10.5 4.5 8.875 5.96875 7.53125ZM12 6.5C9.9375 6.5 8.28125 7.4375 7 8.625C5.78125 9.75 4.96875 11.0625 4.53125 12C4.96875 12.9375 5.78125 14.2812 7 15.4062C8.28125 16.5938 9.9375 17.5 12 17.5C14.0312 17.5 15.6875 16.5938 16.9688 15.4062C18.1875 14.2812 19 12.9375 19.4375 12C19 11.0625 18.1875 9.75 16.9688 8.625C15.6875 7.4375 14.0312 6.5 12 6.5Z"
                      fill="currentColor"
                    />
                  </svg>
                }
              />
            </Tooltip>
            <Tooltip hasArrow label="Edit tweet">
              <IconButton
                aria-label="edit tweet"
                variant="action"
                size="table"
                mr="2"
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.4688 5.5625C20.1562 6.25 20.1562 7.34375 19.4375 8.03125L12.6562 14.8125C12.4375 15.0312 12.1562 15.1875 11.8438 15.2812C10.7812 15.5312 8.46875 16 8.46875 16C8.4375 16 8.4375 16 8.40625 16C8.15625 16 7.9375 15.7812 8 15.5L8.75 12.125C8.8125 11.8125 8.96875 11.5 9.21875 11.2812L15.9688 4.53125C16.3125 4.1875 16.75 4 17.2188 4C17.6562 4 18.0938 4.1875 18.4375 4.53125L19.4688 5.5625ZM18.4062 6.96875C18.5 6.875 18.5 6.71875 18.4062 6.625L17.375 5.59375C17.3125 5.53125 17.25 5.5 17.2188 5.5C17.1562 5.5 17.0938 5.53125 17.0312 5.59375L10.2812 12.3438C10.25 12.375 10.2188 12.4062 10.2188 12.4688L9.8125 14.1875C10.4375 14.0625 11.0938 13.9062 11.5312 13.8125C11.5625 13.8125 11.5938 13.7812 11.625 13.75L18.4062 6.96875ZM17.25 13C17.6562 13 18 13.3438 18 13.75L17.9062 17.75C17.9062 19 16.9062 20 15.6875 20H6.21875C5 20 4 19 4 17.75V8.25C4 7.03125 5 6 6.21875 6H10.75C11.1562 6 11.5 6.34375 11.5 6.75C11.5 7.1875 11.1562 7.5 10.75 7.5H6.25C5.8125 7.5 5.5 7.84375 5.5 8.25V17.75C5.5 18.1875 5.8125 18.5 6.25 18.5H15.75C16.1562 18.5 16.5 18.1875 16.5 17.75V13.75C16.5 13.3438 16.8125 13 17.25 13Z"
                      fill="currentColor"
                    />
                  </svg>
                }
                onClick={async (e) => {
                  e.stopPropagation();
                  console.log("draft id: " + tweet.id);
                  tweetContext.newTweet(tweet, tweet.id);
                  tweetContext.refresher({});
                  tweetContext.setIsTweetTextChanged(true);
                  tweetContext.open();
                  // if (type == "ghost") {
                  //   const db = firebaseClient.firestore();
                  //   await db.collection("users").doc(getAccount(session).id).collection("tweets").doc(tweet.id).update({status: "draft"});
                  // }
                }}
              />
            </Tooltip>
            {type == "drafts" && (
              <Tooltip hasArrow label="Re-write tweet">
                <IconButton
                  aria-label="re-write tweet"
                  variant="action"
                  size="table"
                  mr="2"
                  icon={AIVariationsIcon}
                  isLoading={isLoadingVariation}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAiGeneration();
                  }}
                />
              </Tooltip>
            )}
          </>
        )}

        {(tweet.userId === getAccount(session)?.id ||
          ["ghost"].includes(type)) && (
          <IconButton
            aria-label="Delete tweet"
            variant="actionDestructive"
            size="table"
            mr={2}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.25 6.5C18.6562 6.5 19 6.84375 19 7.25C19 7.6875 18.6562 8 18.25 8H18L17.3125 18.5938C17.2812 19.4062 16.625 20 15.8125 20H8.15625C7.34375 20 6.6875 19.4062 6.65625 18.5938L6 8H5.71875C5.3125 8 5 7.625 5 7.25C5 6.84375 5.3125 6.5 5.71875 6.5H8.0625L9.125 4.75C9.40625 4.28125 9.875 4 10.4062 4H13.5625C14.0938 4 14.5625 4.28125 14.8438 4.75L15.9062 6.5H18.25ZM10.4062 5.5L9.8125 6.5H14.1562L13.5625 5.5H10.4062ZM15.8125 18.5L16.4688 8H7.5L8.15625 18.5H15.8125Z"
                  fill="currentColor"
                />
              </svg>
            }
            isLoading={isDeleting}
            onClick={async (e) => {
              e.stopPropagation();
              if (type === "published") {
                onOpenDelete();
              } else {
                try {
                  setIsDeleting(true);
                  const db = firebaseClient.firestore();
                  await db
                    .collection("users")
                    .doc(getAccount(session)?.id)
                    .collection("tweets")
                    .doc(tweet.id)
                    .delete();

                  setIsDeleting(false);
                  onDelete(tweet.id);
                } catch (e) {
                  setIsDeleting(false);
                  console.log("Error in deleting draft: ", e);
                  toast.error("Error in deleting draft: " + e.message);
                }
              }
            }}
          />
        )}
        {!isGhostwriter && ["drafts", "ghost"].includes(type) && (
          <Button
            variant="secondary"
            size="sm"
            // fontSize="xs"
            ml={1}
            onClick={(e) => {
              e.stopPropagation();
              handleAddToQueue();
            }}
            leftIcon={PlusIcon}
          >
            Add To Queue
          </Button>
        )}
        {/* {!isGhostwriter && ["ghost"].includes(type) && (
          <Button
            variant={tweet.statusGhost == "rejected" ? "ghost" : "outline"}
            size="xs"
            variant={"primary"}
            ml={2}
            onClick={(e) => {
              e.stopPropagation();
              onOpenRevision();
            }}
            color={tweet.statusGhost == "rejected" ? "red.400" : iconColor}
          >
            <Icon as={ImCross} />
          </Button>
        )} */}
        {!isGhostwriter && ["ghost"].includes(type) && (
          <Button
            // variant={tweet.statusGhost == "ready" ? "ghost" : "outline"}
            variant="action"
            size="table"
            ml={2}
            isLoading={isLoadingApprove}
            onClick={async (e) => {
              e.stopPropagation();
              setIsLoadingApprove(true);
              await approve(tweet);
              setIsLoadingApprove(false);
            }}
            color={tweet.statusGhost == "ready" ? "green.400" : iconColor}
          >
            <Icon as={BsCheckLg} />
          </Button>
        )}
      </Flex>
      <SettingsPopup
        isOpen={isOpenSettings}
        onClose={onCloseSettings}
        tweet={tweet}
        hideAdvancedButton={type === "drafts"}
        showAddToQueue={!isGhostwriter && ["drafts", "ghost"].includes(type)}
        isGhostwriter={isGhostwriter}
        handleAddToQueue={handleAddToQueue}
      />
      <DeleteTweet
        tweetId={tweet.id_str}
        onClose={onCloseDelete}
        isOpen={isOpenDelete}
        tweet={tweet}
      />
      <StatsPopup isOpen={isOpenStats} onClose={onCloseStats} tweet={tweet} />
      <Reports isOpen={isOpenReports} onClose={onCloseReports} />
      <DmHistoryPopup
        idTweet={tweet.id}
        onClose={onCloseDmHistory}
        isOpen={isOpenDmHistory}
      />
      <ConfirmPopup
        isOpen={isOpenRevision}
        onClose={onCloseRevision}
        title="Confirm Rejection"
        allowClickBackground={true}
        body={
          "Can you confirm you want to ask a revision for this content? It is recommended to also add comments to guide the revision."
        }
        callback={() => reject(tweet)}
      />
    </Flex>
  );
}
